import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const StatsModal = ({ onClose }) => {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sessionsRes, statsRes] = await Promise.all([
          fetch(`${API}/api/game-sessions?limit=20`).then((r) => r.json()),
          fetch(`${API}/api/stats`).then((r) => r.json())
        ]);
        if (cancelled) return;
        setSessions(sessionsRes);
        setStats(statsRes);
      } catch (e) {
        console.error('Failed to fetch stats:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center p-3 sm:p-6 md:p-8"
      data-testid="stats-modal"
      style={{ background: 'rgba(2,7,9,0.88)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="rc-panel rc-enter"
        style={{
          width: '100%',
          maxWidth: 960,
          maxHeight: '92vh',
          overflowY: 'auto',
          padding: 'clamp(14px, 2.5vh, 28px) clamp(16px, 3vw, 32px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div className="rc-tag" style={{ marginBottom: 6 }}>Roadcast Telemetry</div>
            <h2
              style={{
                fontFamily: 'var(--rc-font-display)',
                fontWeight: 900,
                letterSpacing: '-0.02em',
                fontSize: 'clamp(28px, 3.4vw, 42px)',
                lineHeight: 1,
                color: 'var(--rc-text-primary)'
              }}
            >
              SESSION <span style={{ color: 'var(--rc-teal-bright)' }}>HISTORY</span>
            </h2>
          </div>
          <button
            data-testid="close-stats-button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: '1px solid var(--rc-border-subtle)',
              borderRadius: 8,
              width: 36,
              height: 36,
              color: 'var(--rc-text-secondary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'border-color 180ms ease, color 180ms ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--rc-teal)';
              e.currentTarget.style.color = 'var(--rc-text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--rc-border-subtle)';
              e.currentTarget.style.color = 'var(--rc-text-secondary)';
            }}
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--rc-text-secondary)', fontFamily: 'var(--rc-font-mono)' }}>
            Loading telemetry…
          </div>
        ) : (
          <>
            {/* Session table */}
            {sessions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--rc-text-secondary)', fontFamily: 'var(--rc-font-mono)' }}>
                No sessions recorded yet.
              </div>
            ) : (
              <div style={{ overflow: 'auto', maxHeight: '38vh', border: '1px solid var(--rc-border-subtle)', borderRadius: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, background: 'rgba(8,20,24,0.98)', backdropFilter: 'blur(6px)' }}>
                      {['Drinks', 'Time', 'Distance', 'Crashes', 'Date'].map((h) => (
                        <th
                          key={h}
                          className="rc-tag"
                          style={{
                            textAlign: 'left',
                            padding: '12px 16px',
                            borderBottom: '1px solid var(--rc-border-subtle)'
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s, idx) => (
                      <tr
                        key={s.id || idx}
                        style={{
                          borderBottom: '1px solid rgba(34,211,195,0.08)',
                          transition: 'background 180ms ease'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(34,211,195,0.05)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <td style={cellStyle}>
                          <DrinkTag count={s.drink_count} />
                        </td>
                        <td style={cellStyle}>{s.time_survived.toFixed(1)}s</td>
                        <td style={cellStyle}>{s.distance_traveled.toFixed(1)} m</td>
                        <td style={cellStyle}>{s.crashes}</td>
                        <td style={{ ...cellStyle, color: 'var(--rc-text-muted)' }}>
                          {new Date(s.timestamp).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const cellStyle = {
  padding: '12px 16px',
  fontFamily: 'var(--rc-font-mono)',
  fontSize: 13,
  color: 'var(--rc-text-primary)'
};

const DrinkTag = ({ count }) => {
  const label = count === 0 ? 'SOBER' : count <= 3 ? 'BUZZED' : 'VERY DRUNK';
  const color =
    count === 0 ? 'var(--rc-teal)' :
    count <= 3 ? 'var(--rc-amber)' :
    'var(--rc-danger)';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: 999,
        border: `1px solid ${color}`,
        color,
        fontFamily: 'var(--rc-font-display)',
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: '0.08em'
      }}
    >
      {label} · {count}
    </span>
  );
};

export default StatsModal;
